/**
 * Premier envoi, téléchargement initial et rebootstrap (SYNC_DESIGN §6.8).
 *
 *  - serveur vide : le poste envoie tout (lots de 50 par le moteur) ;
 *  - serveur non vide : instantané téléchargé et comparé ; si les deux côtés ont des données, le SERVEUR gagne et la
 *    copie locale différente part en quarantaine récupérable (« À examiner ») ;
 *  - reprise possible à tout moment : la phase est mémorisée côté SQLite, chaque paquet est idempotent.
 */
import { SYNC_LIMITS, SYNC_RESOURCES, type SyncStatusResponse } from './SyncProtocol';
import type { SyncApiPort, SyncOutboxPort } from './SyncPorts';

export interface InitialOutcome {
  downloaded: number;
  replaced: number;
  identical: number;
  uploaded: number;
  /** Serveur vide mais licence en lecture seule : l'envoi attend le renouvellement. */
  waitingForWritableLicence: boolean;
}

export interface SyncInitialDeps {
  api: SyncApiPort;
  outbox: SyncOutboxPort;
  yieldToUi?: () => Promise<void>;
  onProgress?: (progress: { done: number; total: number } | null) => void;
}

function sum(counts: Record<string, number> | undefined): number | null {
  if (!counts) return null;
  return Object.values(counts).reduce((a, b) => a + b, 0);
}

export class SyncInitialSync {
  constructor(private readonly deps: SyncInitialDeps) {}

  async run(input: { status: SyncStatusResponse; readOnly: boolean }): Promise<InitialOutcome> {
    const { api, outbox } = this.deps;
    const outcome: InitialOutcome = {
      downloaded: 0,
      replaced: 0,
      identical: 0,
      uploaded: 0,
      waitingForWritableLicence: false,
    };
    await outbox.updateMeta({ phase: 'bootstrapping' });
    // Le curseur de départ est lu AVANT toute transmission : tout ce qui bouge ensuite sera rejoué par `pull`.
    const withCounts = await api.status({ includeCounts: true });
    const head = withCounts.head_cursor;
    const epoch = withCounts.epoch;
    const serverTotal = sum(withCounts.counts);

    if (serverTotal === 0) {
      const local = sum(await outbox.localCounts()) ?? 0;
      if (local > 0 && input.readOnly) {
        await outbox.updateMeta({ phase: 'uninitialized' });
        outcome.waitingForWritableLicence = true;
        return outcome;
      }
      const { queued } = await outbox.enqueueAllLocal();
      outcome.uploaded = queued;
      await outbox.initialMergeFinish(head, epoch);
      return outcome;
    }

    const total = serverTotal ?? 0;
    let done = 0;
    this.deps.onProgress?.({ done, total });
    for (const resource of SYNC_RESOURCES) {
      let after: string | null = null;
      for (;;) {
        const page = await api.bootstrap({ resource, after, limit: SYNC_LIMITS.bootstrapPage });
        for (let i = 0; i < page.items.length; i += SYNC_LIMITS.applyChunk) {
          const part = page.items.slice(i, i + SYNC_LIMITS.applyChunk);
          const res = await outbox.initialMergeBatch(part, 'server_wins');
          outcome.downloaded += res.downloaded;
          outcome.replaced += res.replaced;
          outcome.identical += res.identical;
          done += part.length;
          this.deps.onProgress?.({ done, total: Math.max(total, done) });
          await this.deps.yieldToUi?.();
        }
        if (page.done || !page.next_after) break;
        after = page.next_after;
      }
    }
    const fin = await outbox.initialMergeFinish(head, epoch);
    outcome.uploaded = fin.queued;
    this.deps.onProgress?.(null);
    return outcome;
  }

  /**
   * Curseur expiré (poste absent trop longtemps) ou base serveur restaurée (époque changée).
   * La file locale est conservée intacte.
   */
  async rebootstrap(mode: 'cursor_expired' | 'epoch_changed', status: SyncStatusResponse): Promise<void> {
    const { api, outbox } = this.deps;
    await outbox.beginRebootstrap(mode);
    const present: string[] = [];
    const covered = new Set<string>();
    for (const resource of SYNC_RESOURCES) {
      let after: string | null = null;
      for (;;) {
        const page = await api.bootstrap({ resource, after, limit: SYNC_LIMITS.bootstrapPage });
        for (let i = 0; i < page.items.length; i += SYNC_LIMITS.applyChunk) {
          const part = page.items.slice(i, i + SYNC_LIMITS.applyChunk);
          if (mode === 'epoch_changed') await outbox.initialMergeBatch(part, 'local_wins');
          else await outbox.applyRemoteBatch(part, { markPull: false });
          await this.deps.yieldToUi?.();
        }
        for (const item of page.items) {
          present.push(`${item.resource}/${item.id}`);
          covered.add(item.resource);
        }
        if (page.done || !page.next_after) break;
        after = page.next_after;
      }
    }
    await outbox.reconcileSnapshot(present, mode, [...covered]);
    await outbox.initialMergeFinish(status.head_cursor, status.epoch);
  }
}
